import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Share2, Copy, Users, Trophy, Gift, Loader2, Check, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReferralPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        fetch(`${API}/api/referrals/stats`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/referrals/leaderboard`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setStats(s);
      setLeaderboard(l.leaderboard || []);
    } catch (err) { logger.error("Operation failed:", err); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyLink = () => {
    if (stats?.referral_link) {
      navigator.clipboard.writeText(stats.referral_link);
      toast.success('Referral link copied!');
    }
  };

  const shareReferral = () => {
    const text = `Join SendPI and improve your communication intelligence! Use my referral link: ${stats?.referral_link || 'https://sendpi.ai'} #SendPI`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="space-y-8" data-testid="referral-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Refer & Earn</h1>
        <p className="text-white/55">Invite friends and earn bonus credits</p>
      </div>

      {/* Referral Link Card */}
      <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-purple-500/20 rounded-2xl p-6">
        <h3 className="text-white font-medium mb-3">Your Referral Link</h3>
        <div className="flex gap-2">
          <Input readOnly value={stats?.referral_link || ''} className="bg-white/5 border-white/10 text-white font-mono text-sm" data-testid="referral-link" />
          <Button onClick={copyLink} className="bg-white/10 text-white hover:bg-white/20 shrink-0" data-testid="copy-referral">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={shareReferral} size="sm" className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-full px-4 text-xs">
            <Share2 className="w-3 h-3 mr-1" /> Share
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Referrals', value: stats?.total_referrals || 0, icon: Users, color: 'text-cyan-400' },
          { label: 'Signed Up', value: stats?.free_signups || 0, icon: Check, color: 'text-emerald-400' },
          { label: 'Upgraded', value: stats?.paid_upgrades || 0, icon: Gift, color: 'text-purple-400' },
          { label: 'Credits Earned', value: stats?.total_credits_earned || 0, icon: Trophy, color: 'text-amber-400' },
        ].map(s => (
          <div key={s.label} className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
            <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
            <p className="text-2xl font-bold text-white">{s.value}</p>
            <p className="text-white/40 text-sm">{s.label}</p>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <h3 className="text-white font-medium mb-4">How It Works</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { step: '1', title: 'Share Your Link', desc: 'Copy your unique referral link and share it with friends and colleagues' },
            { step: '2', title: 'Friend Signs Up', desc: 'When they sign up using your link, both of you earn 500 bonus credits' },
            { step: '3', title: 'Climb the Board', desc: 'Top referrers get featured on the leaderboard and earn extra rewards' },
          ].map(s => (
            <div key={s.step} className="text-center p-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm mx-auto mb-3">{s.step}</div>
              <h4 className="text-white font-medium text-sm mb-1">{s.title}</h4>
              <p className="text-white/40 text-xs">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6" data-testid="leaderboard">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" /> Top Referrers
          </h3>
          <div className="space-y-2">
            {leaderboard.map((r, i) => (
              <div key={r.referral_code || `lb-${r.rank}`} className={`flex items-center justify-between p-3 rounded-lg ${i < 3 ? 'bg-amber-500/5 border border-amber-500/10' : 'bg-white/5'}`}>
                <div className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-amber-400 text-black' : i === 1 ? 'bg-gray-300 text-black' : i === 2 ? 'bg-orange-400 text-black' : 'bg-white/10 text-white/50'
                  }`}>
                    {r.rank}
                  </span>
                  <span className="text-white text-sm">{r.display_name}</span>
                </div>
                <span className="text-white/70 text-sm font-medium">{r.total_referrals} referrals</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Referrals */}
      {stats?.referrals?.length > 0 && (
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
          <h3 className="text-white font-medium mb-4">Your Referral History</h3>
          <div className="space-y-2">
            {stats.referrals.map((r, i) => (
              <div key={r.referred_user_id || `ref-${r.created_at}`} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white text-sm">{r.referred_email || 'User'}</p>
                  <p className="text-white/30 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</p>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  r.status === 'upgraded' ? 'bg-purple-500/20 text-purple-400' : 'bg-emerald-500/20 text-emerald-400'
                }`}>{r.status || 'signed_up'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
